import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Divider,
  Row,
  Col,
  Button,
  Radio,
  Collapse,
  Form,
  message
} from 'antd';
import { NationalDataPlatformApi } from 'client/components/schema/integrations/pdnd/NationalDataPlatformApi';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { PageFullRow } from 'client/ui/layout/page/PageFullRow';
import { RispostaE002OK } from 'server/schema/integrations/pdnd/schema/schemas';
import { RouteComponentProps } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import { PDNDFiscalCodeForm } from './form/PDNDFiscalCodeForm';
import { PDNDPersonalDetailsForm } from './form/PDNDPersonalDetailsForm';
import { PDNDIdANPRForm } from './form/PDNDIdANPRForm';
import {
  IPDNDVerificationRequest,
  PDNDVerificationLocationType,
  pdndVerificationRequestValidator,
  PDNDVerificationType
} from 'common/dto/PDNDVerificationDto';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { RadioInputItem } from 'client/ui/form/input/RadioInputItem';
import { FormErrorsNotification } from 'client/ui/form/errors/FormErrorsNotification';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { DatePickerInputItem } from 'client/ui/form/input/DatePickerInputItem';
import { PDNDProfileView } from './result/PDNDProfileView';
import { PDNDResponseListView } from './result/PDNDResponseListView';
import { VerticalSpace } from 'client/ui/space/VerticalSpace';
import { CheckboxInputItem } from 'client/ui/form/input/CheckboxInputItem';
import { PDNDForeignCountryLoadableInput } from 'client/components/schema/integrations/pdnd/input/PDNDForeignCountryLoadableInput';
import { PDNDMunicipalityLoadableInput } from 'client/components/schema/integrations/pdnd/input/PDNDMunicipalityLoadableInput';

const { Title } = Typography;
const { Panel } = Collapse;

export interface INationalDataPlatformVerificationPageProps
  extends RouteComponentProps {}

/**
 * Pagina dell'Accertamento Residenza (C020)
 */
export function NationalDataPlatformVerificationPage(
  props: INationalDataPlatformVerificationPageProps
) {
  const [runVerification, { loading }] = useApiMutation(
    NationalDataPlatformApi.verification,
    {
      data: {},
      invalidates: []
    }
  );
  const { t } = useTranslation();

  // Caricamento iniziale dei componenti
  useEffect(() => {
    PDNDForeignCountryLoadableInput.preload();
    PDNDMunicipalityLoadableInput.preload();
  }, []);

  // const [type, setType] = useState<PDNDVerificationType | undefined>(undefined);

  const [profile, setProfile] = useState<RispostaE002OK | null>(null);
  const handleVerification = useCallback(
    async (rawValues: IPDNDVerificationRequest) => {
      try {
        const body = await pdndVerificationRequestValidator.validate(
          rawValues,
          {
            context: {
              // Trasforma i booleani in stringhe es. `true` -> '1', come
              // richiesto dal servizio PDND
              isSubmit: true
            }
          }
        );
        const result = await runVerification({
          data: {
            body
          }
        });
        setProfile(result.data);
        document
          .getElementById('pdnd-response')
          ?.scrollIntoView({ behavior: 'smooth' });
      } catch (e) {
        console.error('Errore durante la verifica', { e });
      }
    },
    []
  );
  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('verification.c020.breadcrumb', 'Accertamento Residenza')
  });

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };
  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };

  return (
    <FrontLayout isUserPage>
      <Title level={3}>
        {t('verification.c020.page.title', 'Accertamento Residenza')}
      </Title>
      <PageFullRow>
        <Col span={24}>
          <div>
            {/* Form di accertamnento C020 */}
            <Formik<IPDNDVerificationRequest>
              initialValues={{
                search: {
                  type: PDNDVerificationType.FiscalCode
                  // locationType: PDNDVerificationLocationType.Italian
                },
                referenceDate: new Date(),
                reason: ''
              }}
              validate={schemaToValidator(pdndVerificationRequestValidator, {
                context: {}
              })}
              validateOnChange={false}
              onSubmit={async rawValues => {
                try {
                  console.log('rawValues', rawValues);
                  const values = await pdndVerificationRequestValidator.validate(
                    rawValues,
                    { context: {} }
                  );

                  await handleVerification(values);
                } catch (e) {
                  console.error('errors', e);
                  message.error(
                    "Si è verificato un errore durante l'accertamento."
                  );
                }
              }}
            >
              {({ isSubmitting, submitForm, dirty, values, resetForm }) => (
                <div>
                  <FormErrorsNotification />
                  <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div style={{ marginBottom: 6 }}>
                      {t(
                        'verification.c020.label.scelta-tipo-di-verifica',
                        'Scelta tipo di verifica:'
                      )}
                    </div>

                    <RadioInputItem
                      name="search.type"
                      buttonStyle="solid"
                      size="large"
                      // onChange={({ target: { value } }) => setType(value)}
                      // defaultValue={type}
                    >
                      <Radio.Button value={PDNDVerificationType.FiscalCode}>
                        {t(
                          'verification.c020.radio.option.per-codice-fiscale',
                          'Per Codice Fiscale'
                        )}
                      </Radio.Button>
                      <Radio.Button value={PDNDVerificationType.Registry}>
                        {t(
                          'verification.c020.radio.option.per-anagrafica-del-cittadino',
                          'Per anagrafica del cittadino'
                        )}
                      </Radio.Button>
                      <Radio.Button value={PDNDVerificationType.Id}>
                        {t(
                          'verification.c020.radio.option.per-id-anpr',
                          'Per ID ANPR'
                        )}
                      </Radio.Button>
                    </RadioInputItem>
                  </div>

                  <FormikForm {...layout}>
                    {values.search?.type == PDNDVerificationType.FiscalCode && (
                      <PDNDFiscalCodeForm layout={layout} />
                    )}
                    {values.search?.type == PDNDVerificationType.Registry && (
                      <PDNDPersonalDetailsForm
                        layout={layout}
                        values={values}
                      />
                    )}
                    {values.search?.type == PDNDVerificationType.Id && (
                      <PDNDIdANPRForm layout={layout} />
                    )}
                    <Divider style={{ marginTop: 0 }} />
                    <TextInputItem
                      name="reason"
                      label="Riferimento del procedimento"
                      placeholder='Es. "Procedimento 10/2024"'
                      itemProps={{
                        help:
                          'Inserire riferimento del procedimento o della richiesta per la quale si sta effettuando la verifica.'
                      }}
                    />

                    <VerticalSpace />
                    <DatePickerInputItem
                      label={'Accertamento alla data del'}
                      name="referenceDate"
                      type={'from'}
                      style={{ width: '100%' }}
                      itemProps={{
                        help:
                          'Inserire una data antecedente per ottenere i dati storici di residenza alla data indicata.'
                      }}
                    />
                    <VerticalSpace />

                    {values.search?.type && (
                      <Form.Item {...tailLayout}>
                        <Button
                          size="large"
                          type="primary"
                          block
                          htmlType="submit"
                          onClick={submitForm}
                          disabled={loading || isSubmitting}
                        >
                          Avvia Verifica
                        </Button>
                        {profile && (
                          <>
                            <VerticalSpace />
                            <Button
                              size="large"
                              block
                              htmlType="reset"
                              onClick={() => {
                                resetForm();
                                setProfile(null);
                              }}
                              disabled={loading || isSubmitting}
                            >
                              Nuova Verifica
                            </Button>
                          </>
                        )}

                        {/* <Button
                          size="large"
                          type="primary"
                          block
                          loading={loading}
                          onClick={() =>
                            handleVerification({
                              search: {
                                codiceFiscale: 'NNOSGT90A01H501I'
                              },
                              reason: 'Verifica anagrafica test'
                            })
                          }
                        >
                          Test
                        </Button> */}
                      </Form.Item>
                    )}
                  </FormikForm>
                </div>
              )}
            </Formik>
          </div>
          <VerticalSpace size={48} />

          <PDNDResponseListView profile={profile} />
          {/* {profile && (
            <div>
              <pre>{JSON.stringify(profile, null, 2)}</pre>
            </div>
          )} */}
        </Col>
      </PageFullRow>
    </FrontLayout>
  );
}
